import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Container,
  Form,
  Grid,
  List,
  Modal,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";

import _ from "lodash";
import cx from "classnames";
import moment from "moment";

import { downloadReport } from "actions/reports";
import { fetchUserGroups } from "actions/userGroups";
import { fetchStreams } from "actions/streams";

import DownloadIcon from "../../../images/download.svg";
import DownloadIconHover from "../../../images/download2.svg";
import CalendarIcon from "../../../images/calendar.svg";

import styles from "./reports.module.scss";

const reportTypes = [
  { type: "master_assets_report", name: "Master Assets" },
  { type: "top_users_report", name: "Top Users" },
  { type: "users_last_active_report", name: "Last Activity Date" },
  {
    type: "lead_generation_conversation_report",
    name: "Lead Generation Conversations",
  },
];

export default function Reports({ account }) {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (account) {
      dispatch(fetchStreams({ account: account.id, limit: -1 }));
      dispatch(fetchUserGroups({ account: account.id, limit: -1 }));
    }
  }, [account]);

  return (
    <Container className={styles.container}>
      <Grid>
        <Grid.Row className={styles.headerContent}>
          <Grid.Column className={styles.title}>
            {account ? account.name : ""}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row columns={3} className={styles.reportsRow}>
          {reportTypes.map((item, index) => {
            return (
              <Grid.Column key={index} className={styles.cardColumn}>
                <Report
                  key={index}
                  report={item}
                  setReport={setReport}
                  setOpen={setOpen}
                />
              </Grid.Column>
            );
          })}
        </Grid.Row>
      </Grid>

      {report && (
        <ReportModal
          open={open}
          setOpen={setOpen}
          report={report}
          account={account}
        />
      )}
    </Container>
  );
}

function Report({ report, setReport, setOpen }) {
  const [downloadIcon, setDownloadIcon] = useState(DownloadIcon);

  return (
    <>
      <Card className={cx(styles.reportCard)}>
        <Card.Content className={cx(styles.reportContent)}>
          <List className={cx(styles.reportList)}>
            <List.Item className={cx(styles.header)}>{report.name}</List.Item>
            <List.Item className={cx(styles.description)}>
              {report.description ? (
                report.description
              ) : (
                <>
                  Deserunt in adipisicing non est dolor id ut laboris. Nonch
                  deserunt voluptate
                </>
              )}
            </List.Item>
            <List.Item
              onClick={(e) => {
                e.preventDefault();
                setReport(report);
                setOpen(true);
              }}
              className={cx(styles.download)}
            >
              <div
                className={styles.downloadIconContainer}
                onMouseEnter={() => setDownloadIcon(DownloadIconHover)}
                onMouseLeave={() => setDownloadIcon(DownloadIcon)}
              >
                <img src={downloadIcon} className={cx(styles.downloadIcon)} />
              </div>
            </List.Item>
          </List>
        </Card.Content>
      </Card>
    </>
  );
}

function ReportModal({ report, account, open, setOpen }) {
  const dispatch = useDispatch();

  const [initialRendering, setInitialRendering] = useState(true);
  const [filterRequest, setFilterRequest] = useState({ account: null });

  const downloading = useSelector((state) =>
    _.get(state, "reports.downloading")
  );
  const success = useSelector((state) => _.get(state, "reports.success"));

  useEffect(() => {
    if (account) {
      setFilterRequest({ ...filterRequest, account: account.id });
    }
  }, [account]);

  useEffect(() => {
    if (!initialRendering && !downloading && success) {
      setOpen(false);
      toast.success(
        `${report.name} report requested to download. Send email once it's completed`
      );
    }
  }, [downloading, success]);

  const download = () => {
    dispatch(
      downloadReport({ type: report.type, filterRequest: filterRequest })
    );
  };

  setTimeout(() => {
    setInitialRendering(false);
  }, [3000]);

  return (
    <>
      <Modal
        size="tiny"
        open={open}
        centered={true}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        className={styles.reportModal}
      >
        <Modal.Content className={cx(styles.content)}>
          <Grid className={styles.reportGrid}>
            <span className={cx(styles.text)}>{report.name}</span>

            <ReportInputs
              report={report}
              filterRequest={filterRequest}
              setFilterRequest={setFilterRequest}
            />

            <Grid.Row className={styles.buttonRow}>
              <Grid.Column className={styles.buttonColumn} textAlign="right">
                <Button
                  onClick={download}
                  className={styles.downloadBtn}
                  content="Download"
                />
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                  }}
                  className={styles.cancelBtn}
                  content="Cancel"
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
      </Modal>
    </>
  );
}

function ReportInputs({ report, filterRequest, setFilterRequest }) {
  const renderInputs = () => {
    if (report) {
      if (report.type === "top_users_report") {
        return (
          <TopUsersInput
            filterRequest={filterRequest}
            setFilterRequest={setFilterRequest}
          />
        );
      } else if (report.type === "lead_generation_conversation_report") {
        return (
          <LeadGenerationInputs
            filterRequest={filterRequest}
            setFilterRequest={setFilterRequest}
          />
        );
      } else {
        return <></>;
      }
    }
  };

  return <>{renderInputs()}</>;
}

function TopUsersInput({ filterRequest, setFilterRequest }) {
  const [userGroupOptions, setUserGroupOptions] = useState([]);

  const loading = useSelector((state) => _.get(state, "userGroups.loading"));
  const userGroups = useSelector((state) => _.get(state, "userGroups.items"));

  useEffect(() => {
    setFilterRequest({ account: filterRequest.account, userGroup: null });
  }, []);

  useEffect(() => {
    if (!loading && userGroups) {
      let options = [];
      userGroups.forEach((item, index) => {
        options.push({ key: index, text: item.name, value: item.id });
      });

      setUserGroupOptions(options);
    }
  }, [loading, userGroups]);

  return (
    <>
      <Grid.Row>
        <Grid.Column>
          <Form>
            <Form.Dropdown
              id="userGroup"
              placeholder="User Group"
              value={filterRequest.userGroup}
              onChange={(e, data) =>
                setFilterRequest({ ...filterRequest, userGroup: data.value })
              }
              selection
              options={userGroupOptions}
            />
          </Form>
        </Grid.Column>
      </Grid.Row>
    </>
  );
}

function LeadGenerationInputs({ filterRequest, setFilterRequest }) {
  const [fromDate, setFromDate] = useState(
    moment()
      .subtract(1, "week")
      .startOf("week")
      .toDate()
  );

  const [toDate, setToDate] = useState(
    moment()
      .subtract(1, "week")
      .endOf("week")
      .toDate()
  );

  useEffect(() => {
    setFilterRequest({
      account: filterRequest.account,
      dateField: "created_date",
      dateFrom: moment(fromDate).format("YYYY-MM-DD"),
      dateTo: moment(toDate).format("YYYY-MM-DD"),
    });
  }, [fromDate, toDate]);

  const onDateChange = (dates) => {
    const [from, to] = dates;

    setFromDate(from);
    setToDate(to);
  };

  return (
    <>
      <Grid.Row>
        <Grid.Column>
          <label className={styles.dateWrapper}>
            <DatePicker
              className={styles.date}
              selectsRange
              selected={fromDate}
              startDate={fromDate}
              endDate={toDate}
              placeholderText="Choose a date"
              popperPlacement="bottom"
              onChange={onDateChange}
            />
          </label>
          <img className={styles.calendar} src={CalendarIcon} alt="" />
        </Grid.Column>
      </Grid.Row>
    </>
  );
}
